import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useBffClient } from './client';
import type { Policy, CreatePolicyRequest, AddRuleRequest } from '../types/policy';

export function usePolicies() {
  const client = useBffClient();
  return useQuery({
    queryKey: ['policies'],
    queryFn: () => client.get<Policy[]>('/bff/policies'),
  });
}

export function usePolicy(policyId: string) {
  const client = useBffClient();
  return useQuery({
    queryKey: ['policies', policyId],
    queryFn: () => client.get<Policy>(`/bff/policies/${policyId}`),
    enabled: !!policyId,
  });
}

export function useCreatePolicy() {
  const client = useBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreatePolicyRequest) => client.post('/bff/policies', request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policies'] });
    },
  });
}

export function useAddRule(policyId: string) {
  const client = useBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: AddRuleRequest) =>
      client.post(`/bff/policies/${policyId}/rules`, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policies', policyId] });
      queryClient.invalidateQueries({ queryKey: ['policies'] });
    },
  });
}

export function useRemoveRule(policyId: string) {
  const client = useBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) =>
      client.delete(`/bff/policies/${policyId}/rules/${ruleId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policies', policyId] });
      queryClient.invalidateQueries({ queryKey: ['policies'] });
    },
  });
}

export function useActivatePolicy() {
  const client = useBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (policyId: string) =>
      client.postVoid(`/bff/policies/${policyId}/activate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policies'] });
    },
  });
}

export function useArchivePolicy() {
  const client = useBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (policyId: string) =>
      client.postVoid(`/bff/policies/${policyId}/archive`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policies'] });
    },
  });
}
